import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  Clock3,
  QrCode,
  Receipt,
  RefreshCw,
  ShoppingCart,
  Store,
  TrendingDown,
  TrendingUp,
  Zap,
} from "lucide-react";
import { api, formatIDR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const REFRESH_INTERVAL_MS = 10_000;

const numberFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat("id-ID", {
  maximumFractionDigits: 1,
});

function formatClock(value) {
  if (!value) return "Belum tersinkron";

  try {
    return new Date(value).toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "Belum tersinkron";
  }
}

function formatDay(value) {
  if (!value) return "-";

  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
    });
  } catch {
    return value;
  }
}

function StatCard({ label, value, detail, icon: Icon, color = "#002FA7", to }) {
  const content = (
    <div className="h-full bg-white p-5 lg:p-6 transition-colors hover:bg-zinc-50">
      <div className="flex items-start justify-between gap-4">
        <div className="overline text-zinc-500">{label}</div>
        <Icon className="h-4 w-4 shrink-0" style={{ color }} strokeWidth={2} />
      </div>
      <div className="mt-3 font-display text-3xl font-black tracking-tighter tabular-nums lg:text-4xl">
        {value}
      </div>
      {detail ? <div className="mt-2 text-xs text-zinc-500">{detail}</div> : null}
    </div>
  );

  return to ? <Link to={to}>{content}</Link> : content;
}

export default function Dashboard() {
  const { user, tenant, subscription } = useAuth();
  const [overview, setOverview] = useState(null);
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadOverview = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setLoading(true);

      try {
        const response = await api.get("/dashboard/overview", {
          params: {
            outlet_id: selectedOutlet || undefined,
            tz_offset_minutes: new Date().getTimezoneOffset(),
          },
        });

        setOverview(response.data);
        setError("");
      } catch (requestError) {
        setError(
          requestError?.response?.data?.detail ||
            "Ringkasan dashboard belum dapat dimuat. Periksa koneksi backend."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [selectedOutlet]
  );

  useEffect(() => {
    loadOverview();

    const timer = window.setInterval(
      () => loadOverview({ silent: true }),
      REFRESH_INTERVAL_MS
    );

    return () => window.clearInterval(timer);
  }, [loadOverview]);

  const today = overview?.today || {};
  const stock = overview?.stock || {};
  const outletSummary = overview?.outlets || {};
  const sales7d = overview?.sales_7d || [];
  const topProducts = overview?.top_products || [];
  const comparison = Number(today.comparison_percent || 0);
  const isPositive = comparison >= 0;
  const activeOutletName = overview?.scope?.outlet_name || "Semua Outlet";
  const maxRevenue = Math.max(
    1,
    ...sales7d.map((row) => Number(row.revenue || 0))
  );
  const maxTopProductRevenue = Math.max(
    1,
    ...topProducts.map((row) => Number(row.revenue || 0))
  );

  return (
    <div className="bg-white p-6 text-zinc-900 lg:p-10">
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="overline text-zinc-500">DASHBOARD</div>
          <h1 className="mt-1 font-display text-4xl font-black tracking-tighter lg:text-5xl">
            Halo, {user?.name?.split(" ")?.[0] || "Mitra"}.
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {tenant?.name || "Mitra"}
            {subscription?.status
              ? ` · ${String(subscription.status).toUpperCase()}`
              : ""}
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <label className="sr-only" htmlFor="dashboard-outlet-filter">
            Pilih outlet
          </label>
          <select
            id="dashboard-outlet-filter"
            value={selectedOutlet}
            onChange={(event) => setSelectedOutlet(event.target.value)}
            className="h-11 min-w-[220px] border border-border bg-white px-3 text-sm outline-none transition-colors focus:border-[#002FA7]"
            data-testid="dashboard-outlet-filter"
          >
            <option value="">Semua Outlet</option>
            {(outletSummary.items || []).map((outlet) => (
              <option key={outlet.id} value={outlet.id}>
                {outlet.name}
                {outlet.active === false ? " (Nonaktif)" : ""}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => loadOverview({ silent: true })}
            disabled={refreshing}
            className="inline-flex h-11 items-center justify-center gap-2 border border-border bg-white px-4 text-xs font-semibold uppercase tracking-widest transition-colors hover:border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="dashboard-refresh"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-6 flex items-start gap-3 border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div>{error}</div>
        </div>
      ) : null}

      <div className="mb-4 flex flex-col gap-2 border-y border-border py-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <span className="overline text-zinc-500">RINGKASAN HARI INI</span>
          <span className="ml-3 text-sm font-semibold text-zinc-900">
            {activeOutletName}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span className="h-2 w-2 bg-emerald-500" />
          Live · Sinkron {formatClock(overview?.generated_at)}
        </div>
      </div>

      <div
        className="mb-8 grid grid-cols-1 grid-borders sm:grid-cols-2 xl:grid-cols-4"
        data-testid="dashboard-overview"
      >
        <StatCard
          label="Penjualan Hari Ini"
          value={loading ? "…" : formatIDR(today.revenue || 0)}
          detail={
            loading
              ? "Memuat perbandingan"
              : `${isPositive ? "+" : ""}${decimalFormatter.format(
                  comparison
                )}% dari kemarin`
          }
          icon={isPositive ? TrendingUp : TrendingDown}
          color={isPositive ? "#16A34A" : "#E11D48"}
          to="/app/sales"
        />
        <StatCard
          label="Transaksi"
          value={loading ? "…" : numberFormatter.format(today.transactions || 0)}
          detail={`${decimalFormatter.format(today.qris_share || 0)}% menggunakan QRIS`}
          icon={Receipt}
          color="#F59E0B"
          to="/app/sales"
        />
        <StatCard
          label="Stok Real-time"
          value={
            loading
              ? "…"
              : stock.available === false
              ? "Tidak tersedia"
              : `${numberFormatter.format(stock.total_units || 0)} item`
          }
          detail={
            stock.available === false
              ? "Fitur tidak termasuk paket aktif"
              : `${numberFormatter.format(stock.total_products || 0)} produk terpantau`
          }
          icon={Boxes}
          color="#002FA7"
          to={stock.available === false ? undefined : "/app/stock"}
        />
        <StatCard
          label="Outlet Aktif"
          value={
            loading
              ? "…"
              : `${numberFormatter.format(outletSummary.active || 0)} lokasi`
          }
          detail={`${numberFormatter.format(outletSummary.total || 0)} outlet dapat diakses`}
          icon={Store}
          color="#E11D48"
          to="/app/outlets"
        />
      </div>

      {stock.available !== false ? (
        <div className="mb-10 grid grid-cols-1 grid-borders md:grid-cols-3">
          <Link to="/app/stock" className="bg-white p-5 transition-colors hover:bg-zinc-50">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="overline text-zinc-500">STOK RENDAH</div>
                <div className="mt-2 font-display text-2xl font-black tracking-tighter tabular-nums">
                  {numberFormatter.format(stock.low_stock_count || 0)} produk
                </div>
              </div>
              <Zap className="h-5 w-5 text-amber-500" />
            </div>
          </Link>
          <Link to="/app/stock" className="bg-white p-5 transition-colors hover:bg-zinc-50">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="overline text-zinc-500">STOK HABIS</div>
                <div className="mt-2 font-display text-2xl font-black tracking-tighter tabular-nums">
                  {numberFormatter.format(stock.out_of_stock_count || 0)} produk
                </div>
              </div>
              <AlertTriangle className="h-5 w-5 text-rose-600" />
            </div>
          </Link>
          <Link to="/app/stock" className="bg-zinc-50 p-5 transition-colors hover:bg-zinc-100">
            <div className="flex h-full items-center justify-between gap-4">
              <div>
                <div className="overline text-zinc-500">MANAJEMEN STOK</div>
                <div className="mt-2 text-sm font-semibold">Lihat stok dan stock opname</div>
              </div>
              <ArrowRight className="h-5 w-5 text-[#002FA7]" />
            </div>
          </Link>
        </div>
      ) : null}

      <div className="mb-10 grid grid-borders lg:grid-cols-3">
        <section className="bg-white p-6 lg:col-span-2">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="overline text-zinc-500">PENJUALAN HARIAN</div>
              <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">
                Performa 7 Hari
              </h2>
            </div>
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <Clock3 className="h-4 w-4 text-[#002FA7]" />
              Data transaksi berstatus lunas
            </div>
          </div>

          {sales7d.length ? (
            <div className="space-y-3" data-testid="dashboard-sales-chart">
              {sales7d.map((row) => {
                const revenue = Number(row.revenue || 0);
                const width = revenue > 0 ? Math.max(2, (revenue / maxRevenue) * 100) : 0;

                return (
                  <div key={row.day} className="grid grid-cols-[72px_1fr_118px] items-center gap-3 sm:grid-cols-[92px_1fr_150px] sm:gap-4">
                    <div className="font-mono-num text-xs text-zinc-500">
                      {formatDay(row.day)}
                    </div>
                    <div className="relative h-7 bg-zinc-100">
                      <div
                        className="absolute inset-y-0 left-0 bg-[#002FA7] transition-[width] duration-500"
                        style={{ width: `${width}%` }}
                        title={`${row.day}: ${formatIDR(revenue)}`}
                      />
                    </div>
                    <div className="text-right font-mono-num text-xs text-zinc-700">
                      {formatIDR(revenue)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="border border-dashed border-border bg-zinc-50 px-6 py-10 text-center">
              <Receipt className="mx-auto h-6 w-6 text-zinc-400" />
              <div className="mt-3 text-sm text-zinc-500">
                Belum ada transaksi dalam tujuh hari terakhir.
              </div>
              <Link
                to="/app/pos"
                className="mt-4 inline-flex items-center gap-2 bg-zinc-900 px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-white"
              >
                <ShoppingCart className="h-4 w-4" /> Mulai transaksi
              </Link>
            </div>
          )}
        </section>

        <section className="bg-white p-6">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <div className="overline text-zinc-500">7 HARI TERAKHIR</div>
              <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">
                Produk Terlaris
              </h2>
            </div>
            <QrCode className="h-5 w-5 text-[#002FA7]" />
          </div>

          {topProducts.length ? (
            <div className="space-y-4">
              {topProducts.slice(0, 5).map((product, index) => {
                const width = Math.max(
                  4,
                  (Number(product.revenue || 0) / maxTopProductRevenue) * 100
                );

                return (
                  <div key={product.product_id || `${product.name}-${index}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-6 font-display text-lg font-black tabular-nums text-zinc-400">
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="truncate text-sm font-semibold">{product.name}</div>
                          <div className="shrink-0 font-mono-num text-xs">
                            {formatIDR(product.revenue || 0)}
                          </div>
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">
                          {numberFormatter.format(product.qty || 0)} terjual
                        </div>
                      </div>
                    </div>
                    <div className="ml-9 mt-2 h-1 bg-zinc-100">
                      <div className="h-full bg-[#002FA7]" style={{ width: `${width}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-zinc-500">Belum ada data produk terjual.</div>
          )}

          <Link
            to="/app/reports"
            className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#002FA7] hover:underline"
          >
            Lihat laporan <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <section>
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <div className="overline text-zinc-500">OPERASIONAL</div>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tighter">
              Status Outlet
            </h2>
          </div>
          <Link
            to="/app/outlets"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#002FA7] hover:underline"
          >
            Kelola outlet <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-borders md:grid-cols-2 xl:grid-cols-3">
          {(outletSummary.items || []).slice(0, 6).map((outlet) => (
            <button
              key={outlet.id}
              type="button"
              onClick={() => setSelectedOutlet(outlet.id)}
              className="bg-white p-5 text-left transition-colors hover:bg-zinc-50"
            >
              <div className="flex items-start justify-between gap-4">
                <Store className="h-5 w-5 text-[#002FA7]" />
                <div
                  className={`text-[10px] font-semibold uppercase tracking-widest ${
                    outlet.active === false ? "text-zinc-400" : "text-emerald-600"
                  }`}
                >
                  {outlet.active === false ? "Nonaktif" : "Aktif"}
                </div>
              </div>
              <div className="mt-3 font-display text-xl font-bold tracking-tight">
                {outlet.name}
              </div>
              <div className="mt-1 truncate text-xs text-zinc-500">
                {outlet.address || "Alamat belum diisi"}
              </div>
            </button>
          ))}

          {!outletSummary.items?.length ? (
            <div className="bg-zinc-50 p-8 text-center md:col-span-2 xl:col-span-3">
              <div className="text-sm text-zinc-600">Belum ada outlet yang dapat diakses.</div>
              <Link
                to="/app/outlets"
                className="mt-4 inline-block bg-zinc-900 px-5 py-2.5 text-xs font-semibold uppercase tracking-widest text-white"
              >
                Kelola outlet
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}
